import type { BikeParkMetadata, MtbTrailRelatedPlace, UpdatePlaceData } from "~/lib/api";
import { PanelSection } from "~/components/ui/panel-section";
import { Switch } from "~/components/ui/switch";
import { BikeParkAccessPills } from "./BikeParkAccessPills";
import { TrailDifficultySummary } from "./TrailDifficultySummary";
import { ACCESS_LABELS } from "./bikeParkLabels";

interface BikeParkInfoSectionProps {
  metadata: BikeParkMetadata | null;
  trails?: MtbTrailRelatedPlace[];
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
}

export function BikeParkInfoSection({ metadata, trails = [], canEdit, onUpdate }: BikeParkInfoSectionProps) {
  const { liftAccess, shuttleAccess } = metadata ?? {};
  const hasAccess = !!(liftAccess || shuttleAccess);
  const hasTrails = trails.length > 0;

  if (!canEdit && !hasAccess && !hasTrails) return null;

  const updateMetadata = (updates: Partial<BikeParkMetadata>) => {
    onUpdate?.({ metadata: { ...metadata, ...updates } });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Park Info</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <>
              <label className="flex items-center justify-between gap-3 text-sm">
                {ACCESS_LABELS.liftAccess}
                <Switch
                  checked={!!liftAccess}
                  onCheckedChange={(checked) => updateMetadata({ liftAccess: checked })}
                  aria-label={ACCESS_LABELS.liftAccess}
                />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                {ACCESS_LABELS.shuttleAccess}
                <Switch
                  checked={!!shuttleAccess}
                  onCheckedChange={(checked) => updateMetadata({ shuttleAccess: checked })}
                  aria-label={ACCESS_LABELS.shuttleAccess}
                />
              </label>
            </>
          ) : (
            <>
              {metadata && <BikeParkAccessPills metadata={metadata} />}
              <TrailDifficultySummary trails={trails} />
            </>
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}
